import { ButtonLink } from '@domgen/base-design-system-2.0/atoms/ButtonLink';
import { Modal } from '@domgen/base-design-system-2.0/molecules/Modal';
import { PosthogFeatureFlags } from '@domgen/shared/enums/featureFlags';
import { gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { usePostHog } from '@posthog/react';
import { useEffect, useState } from 'react';
import { Phone } from '../../../assets/icons/Phone';
import { CALL_US_MODAL_TEL_HREF } from '../../../const/callUs';
import { PostHogEvent } from '../../../enums/postHogEvents';
import { StoreEvent } from '../../../enums/storeEvent';
import { capturePostHogEvent } from '../../../services/posthog/capture';
import { useAppState } from '../../../store/selectors/useAppState';
import { useGaEventData } from '../../../store/selectors/useGaEventData';
import { useUrlParams } from '../../../store/selectors/useUrlParams';
import { store } from '../../../store/store';
import { getSystemCode } from '../../../utils/getSystemCode';
import { formApplianceName } from '../../../utils/store/formApplianceName';
import { formCategoryName } from '../../../utils/store/formCategoryName';
import { callUsModalContent } from '../constants/callUsModalContent';

const CALL_US_MODAL_DELAY_MS = 60_000;

const GA_PAGE_NAME = 'protection-details-popup-call-us';

const styles = {
  content: 'tablet:w-127.5',
  title: 'text-h4 text-text-headline mb-space-400',
  body: 'flex flex-col gap-space-600 text-text-body',
  intro: 'mb-space-500',
  points: 'list-disc ps-space-600 text-body',
  icon: 'flex w-6 h-6 [&>svg]:w-6 [&>svg]:h-6',
};

type CallUsModalProps = {
  blocked: boolean;
  quoteId: string;
};

type UseCallUsModalOptions = {
  blocked: boolean;
  onOpen: () => void;
};

const useCallUsModal = ({ blocked, onOpen }: UseCallUsModalOptions) => {
  const posthog = usePostHog();
  const { callUsModalShown } = useAppState();
  const [delayElapsed, setDelayElapsed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (callUsModalShown) return;
    const timer = setTimeout(() => setDelayElapsed(true), CALL_US_MODAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [callUsModalShown]);

  useEffect(() => {
    if (!delayElapsed || blocked || callUsModalShown) return;
    if (posthog.getFeatureFlag(PosthogFeatureFlags.CALL_US_MODAL) !== 'test') return;

    store.send({ type: StoreEvent.CALL_US_MODAL_SHOWN });
    setIsOpen(true);
    onOpen();
  }, [delayElapsed, blocked, callUsModalShown, posthog, onOpen]);

  return { isOpen, close: () => setIsOpen(false) };
};

export const CallUsModal = ({ blocked, quoteId }: CallUsModalProps) => {
  const posthog = usePostHog();
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const {
    experimentName,
    experimentVariant,
    netFirstPaymentAmount: offerPrice,
  } = useGaEventData() || {};
  const brand = import.meta.env.VITE_CLIENT_FRIENDLY_NAME.trim();

  const gaFields = () => ({
    pagename: GA_PAGE_NAME,
    category: 'registration-pages',
    type: 'STANDARD',
    regSystemCode: getSystemCode(urlParams?.rs),
    offerPrice,
    applianceName: formApplianceName.get() ?? '',
    applianceCategory: formCategoryName.get() ?? '',
    quoteId,
    appSessionID: session?.id,
    experimentName,
    experimentVariant,
  });

  const gaModalEvent = (eventAction: string) =>
    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction,
      eventLabel: callUsModalContent.cta,
      ...gaFields(),
    });

  const captureModalEvent = (eventName: PostHogEvent) =>
    capturePostHogEvent(posthog, eventName, store.get(), { quoteId });

  const { isOpen, close } = useCallUsModal({
    blocked,
    onOpen: () => {
      gaPageView({ event: 'spa-pageview', ...gaFields() });
      captureModalEvent(PostHogEvent.CALL_US_MODAL_DISPLAYED);
    },
  });

  const handleClose = () => {
    gaModalEvent('Close clicked');
    captureModalEvent(PostHogEvent.CALL_US_MODAL_CLOSED);
    close();
  };

  const handleCallClick = () => {
    gaModalEvent('call-us-clicked');
    captureModalEvent(PostHogEvent.CALL_US_MODAL_CALL_CLICKED);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className={styles.content}>
        <h2 className={styles.title}>{callUsModalContent.title}</h2>
        <div className={styles.body}>
          <div>
            <p className={styles.intro}>{callUsModalContent.getIntro(brand)}</p>
            <ul className={styles.points}>
              {callUsModalContent.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
          <ButtonLink
            href={CALL_US_MODAL_TEL_HREF}
            onClick={handleCallClick}
            leftIcon={
              <span className={styles.icon}>
                <Phone />
              </span>
            }
          >
            {callUsModalContent.cta}
          </ButtonLink>
        </div>
      </div>
    </Modal>
  );
};
